// src/pages/webhook/WebhookDetailPage.tsx
// Webhook endpoint detail: inline-editable info, stats, recent deliveries, test, secret rotation.
// The "Endpoint details" card is a property grid — no /edit route, autosave per field.
// See docs/ux/14-forms.md.

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useWebhook, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Badge,
  Alert,
  ErrorAlert,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from '@react/components';
import { FormRow, InlineEditText } from '@/components/form';
import { Select } from '@/components/Select';
import type { WebhookEndpoint, WebhookDelivery, WebhookStats } from '@core/types/webhook';
import {
  getEndpointStatusDisplay,
  getDeliveryStatusDisplay,
  formatDateTime,
  formatDuration,
  formatSuccessRate,
  canDisable,
  canEnable,
  canDelete,
  canTest,
  canEdit,
  needsAuthConfig,
} from '../../features/webhooks/logic';
import { EventTypeChips } from '../../features/webhooks/EventTypeChips';

const AUTH_TYPE_OPTIONS = [
  { value: 'WEBHOOK_AUTH_TYPE_HMAC', label: 'HMAC SHA-256' },
  { value: 'WEBHOOK_AUTH_TYPE_BEARER', label: 'Bearer Token' },
  { value: 'WEBHOOK_AUTH_TYPE_BASIC', label: 'Basic Auth' },
  { value: 'WEBHOOK_AUTH_TYPE_HEADER', label: 'Custom Header' },
  { value: 'WEBHOOK_AUTH_TYPE_NONE', label: 'None' },
];

// --- Inline SVG icons ---

function CopyIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
    </svg>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

// --- Stats Card ---

function StatsCard({ label, value, subtext }: { label: string; value: string; subtext?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="text-2xl font-semibold text-foreground mt-1">{value}</p>
      {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </div>
  );
}

// --- Main page ---

export const WebhookDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useWebhook();
  const { t } = useI18n();

  const [endpoint, setEndpoint] = useState<WebhookEndpoint | null>(null);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [availableEventTypes, setAvailableEventTypes] = useState<string[]>([]);
  const [fieldError, setFieldError] = useState<unknown>(null);

  useEffect(() => {
    if (!id) return;
    loadEndpoint();
    loadStats();
    loadDeliveries();
    store
      .ListEventTypes()
      .then((resp) => {
        const types = resp.event_types?.items?.map((et: { type: string }) => et.type) ?? [];
        setAvailableEventTypes(types);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEndpoint = async () => {
    if (!id) return;
    try {
      const resp = await store.GetEndpoint(id);
      setEndpoint(resp.endpoint ?? null);
    } catch {
      // Error handled by store
    }
  };

  const loadStats = async () => {
    if (!id) return;
    try {
      const resp = await store.GetEndpointStats(id);
      setStats(resp.stats ?? null);
    } catch {
      // Stats are optional, don't block the page
    }
  };

  const loadDeliveries = async () => {
    if (!id) return;
    try {
      await store.ListDeliveries(id, {
        pagination: { cursor: '', limit: 10, direction: 'forward' },
        sort: [],
        date_range: {} as any,
        filters: {} as any,
      });
      setDeliveries(store.items);
    } catch {
      // Error handled by store
    }
  };

  // Per-field autosave: optimistic update, revert + inline error on failure.
  // UpdateEndpoint is a full-replace call — preserve every current field, only
  // change the touched one, exactly as the old edit page did.
  const saveField = async (patch: Partial<WebhookEndpoint>) => {
    if (!id || !endpoint) return;
    const previous = endpoint;
    const merged: WebhookEndpoint = { ...endpoint, ...patch };
    setEndpoint(merged);
    setFieldError(null);
    try {
      await store.UpdateEndpoint(id, {
        endpoint: {
          name: merged.name,
          description: merged.description,
          url: merged.url,
          auth_type: merged.auth_type,
          auth_header: merged.auth_header,
          auth_value: merged.auth_value,
          subscribe_all: merged.subscribe_all,
          event_types: merged.subscribe_all ? [] : merged.event_types,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
      });
    } catch (e) {
      setEndpoint(previous);
      setFieldError(e);
    }
  };

  const handleTest = async () => {
    if (!id) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const resp = await store.TestEndpoint(id, { event_type: 'test.ping' });
      setTestResult({ success: resp.success, message: resp.message });
    } catch {
      setTestResult({ success: false, message: t('webhooks.testFailed') });
    } finally {
      setIsTesting(false);
    }
  };

  const handleToggle = async (enable: boolean) => {
    if (!id) return;
    try {
      if (enable) {
        await store.EnableEndpoint(id, {});
      } else {
        await store.DisableEndpoint(id, {});
      }
      loadEndpoint();
    } catch {
      // Error handled by store
    }
  };

  const handleRotateSecret = async () => {
    if (!id || !window.confirm(t('webhooks.rotateSecretConfirm'))) return;
    setIsRotating(true);
    try {
      const resp = await store.RotateSecret(id, {});
      setNewSecret(resp.new_secret ?? null);
    } catch {
      // Error handled by store
    } finally {
      setIsRotating(false);
    }
  };

  const handleCopySecret = async () => {
    if (newSecret) {
      await navigator.clipboard.writeText(newSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm(t('webhooks.deleteConfirm'))) return;
    try {
      await store.DeleteEndpoint(id);
      navigate('/webhooks');
    } catch {
      // Error handled by store
    }
  };

  if (store.isLoading && !endpoint) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error && !endpoint) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/webhooks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!endpoint) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/webhooks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const status = getEndpointStatusDisplay(endpoint.status);
  // Gates inline editability — a deleted endpoint is read-only.
  const editable = canEdit(endpoint);

  return (
    <div className="space-y-6">
      {/* Breadcrumb + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/webhooks" className="text-muted-foreground hover:text-foreground">
              {t('webhooks.title')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{endpoint.name}</span>
          </nav>
          <div className="flex items-center gap-3">
            <InlineEditText
              value={endpoint.name}
              onCommit={(next) => saveField({ name: next })}
              variant="title"
              aria-label={t('webhooks.fieldName')}
              className={!editable ? 'pointer-events-none' : undefined}
            />
            <Badge variant={status.variant}>{t(status.labelKey)}</Badge>
          </div>
          <InlineEditText
            value={endpoint.description ?? ''}
            onCommit={(next) => saveField({ description: next })}
            allowEmpty
            placeholder={t('webhooks.fieldDescriptionPlaceholder')}
            className={`mt-1 text-muted-foreground ${!editable ? 'pointer-events-none' : ''}`}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {canTest(endpoint) && (
            <Button variant="secondary" onClick={handleTest} disabled={isTesting}>
              <PlayIcon className="h-4 w-4 mr-2" />
              {isTesting ? t('webhooks.testing') : t('webhooks.testEndpoint')}
            </Button>
          )}
          {canDisable(endpoint) && (
            <Button variant="secondary" onClick={() => handleToggle(false)}>
              {t('webhooks.disable')}
            </Button>
          )}
          {canEnable(endpoint) && (
            <Button variant="secondary" onClick={() => handleToggle(true)}>
              {t('webhooks.enable')}
            </Button>
          )}
          {canDelete(endpoint) && (
            <Button variant="danger" onClick={handleDelete}>
              {t('common.delete')}
            </Button>
          )}
        </div>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {fieldError != null && <ErrorAlert error={fieldError} dismissible onDismiss={() => setFieldError(null)} />}

      {/* Test result */}
      {testResult && (
        <Alert variant={testResult.success ? 'success' : 'error'} dismissible onDismiss={() => setTestResult(null)}>
          {testResult.message}
        </Alert>
      )}

      {/* Rotated secret */}
      {newSecret && (
        <Alert variant="success" dismissible onDismiss={() => setNewSecret(null)}>
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('webhooks.secretRotatedTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('webhooks.secretCreatedWarning')}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">
                {newSecret}
              </code>
              <Button variant="secondary" size="sm" onClick={handleCopySecret}>
                {copied ? <CheckIcon className="h-4 w-4 text-green-600" /> : <CopyIcon className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard
            label={t('webhooks.statsTotal')}
            value={String(stats.total_deliveries)}
          />
          <StatsCard
            label={t('webhooks.statsSuccessRate')}
            value={formatSuccessRate(stats.success_rate)}
            subtext={`${stats.successful_deliveries} / ${stats.total_deliveries}`}
          />
          <StatsCard
            label={t('webhooks.statsAvgLatency')}
            value={formatDuration(stats.average_latency_ms)}
            subtext={`p95: ${formatDuration(stats.p95_latency_ms)}`}
          />
          <StatsCard
            label={t('webhooks.statsFailed')}
            value={String(stats.failed_deliveries)}
          />
        </div>
      )}

      {/* Endpoint details — inline-edit property grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('webhooks.endpointDetails')}</CardTitle>
            <Button variant="ghost" size="sm" onClick={handleRotateSecret} disabled={isRotating}>
              <RefreshIcon className="h-4 w-4 mr-2" />
              {isRotating ? t('common.loading') : t('webhooks.rotateSecret')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="divide-y divide-border">
          <FormRow label={t('webhooks.fieldUrl')} labelWidth="sm">
            <InlineEditText
              value={endpoint.url}
              onCommit={(next) => saveField({ url: next })}
              aria-label={t('webhooks.fieldUrl')}
              placeholder="https://example.com/webhooks"
              className={`font-mono ${!editable ? 'pointer-events-none' : ''}`}
            />
          </FormRow>

          <FormRow label={t('webhooks.fieldAuthType')} labelWidth="sm">
            <div className="space-y-2">
              <Select
                value={endpoint.auth_type ?? 'WEBHOOK_AUTH_TYPE_HMAC'}
                onChange={(e: { target: { value: string } }) => saveField({ auth_type: e.target.value as WebhookEndpoint['auth_type'] })}
                options={AUTH_TYPE_OPTIONS}
                disabled={!editable}
                fullWidth={false}
                className="w-48"
                aria-label={t('webhooks.fieldAuthType')}
              />
              {needsAuthConfig(endpoint.auth_type) && endpoint.auth_type === 'WEBHOOK_AUTH_TYPE_HEADER' && (
                <input
                  type="text"
                  defaultValue={endpoint.auth_header ?? ''}
                  disabled={!editable}
                  onBlur={(e) => {
                    if (e.target.value !== (endpoint.auth_header ?? '')) saveField({ auth_header: e.target.value });
                  }}
                  placeholder="X-Webhook-Token"
                  className="w-64 rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
                />
              )}
              {needsAuthConfig(endpoint.auth_type) && endpoint.auth_type !== 'WEBHOOK_AUTH_TYPE_HMAC' && (
                <input
                  type="text"
                  defaultValue={endpoint.auth_value ?? ''}
                  disabled={!editable}
                  onBlur={(e) => {
                    if (e.target.value !== (endpoint.auth_value ?? '')) saveField({ auth_value: e.target.value });
                  }}
                  placeholder="your-secret-value"
                  className="w-64 rounded-md border border-input bg-transparent px-2 py-1 text-sm text-foreground"
                />
              )}
            </div>
          </FormRow>

          <FormRow label={t('webhooks.fieldEvents')} labelWidth="sm">
            <EventTypeChips
              availableTypes={availableEventTypes}
              selected={endpoint.event_types ?? []}
              subscribeAll={!!endpoint.subscribe_all}
              disabled={!editable}
              onSubscribeAllChange={(v) => saveField({ subscribe_all: v })}
              onToggle={(et) => {
                const current = endpoint.event_types ?? [];
                const next = current.includes(et) ? current.filter((x) => x !== et) : [...current, et];
                saveField({ event_types: next });
              }}
            />
          </FormRow>

          <FormRow label={t('webhooks.fieldFailures')} labelWidth="sm">
            {endpoint.consecutive_failures > 0 ? (
              <span className="text-sm font-medium text-destructive">{endpoint.consecutive_failures}</span>
            ) : (
              <span className="text-sm text-foreground">0</span>
            )}
          </FormRow>

          {endpoint.disabled_reason && (
            <FormRow label={t('webhooks.fieldDisabledReason')} labelWidth="sm">
              <span className="text-sm text-destructive">{endpoint.disabled_reason}</span>
            </FormRow>
          )}

          <FormRow label={t('webhooks.fieldLastSuccess')} labelWidth="sm">
            <span className="text-sm text-foreground">{formatDateTime(endpoint.last_success_at as any)}</span>
          </FormRow>
          <FormRow label={t('webhooks.fieldLastFailure')} labelWidth="sm">
            <span className="text-sm text-foreground">{formatDateTime(endpoint.last_failure_at as any)}</span>
          </FormRow>
          <FormRow label={t('webhooks.fieldCreated')} labelWidth="sm">
            <span className="text-sm text-foreground">{formatDateTime(endpoint.created_at as any)}</span>
          </FormRow>
        </CardContent>
      </Card>

      {/* Recent deliveries */}
      <Card>
        <CardHeader>
          <CardTitle>{t('webhooks.recentDeliveries')}</CardTitle>
          <CardDescription>{t('webhooks.recentDeliveriesDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('webhooks.deliveryStatus')}</TableHead>
                <TableHead>{t('webhooks.deliveryHttpStatus')}</TableHead>
                <TableHead>{t('webhooks.deliveryDuration')}</TableHead>
                <TableHead>{t('webhooks.deliveryAttempt')}</TableHead>
                <TableHead>{t('webhooks.deliveryTimestamp')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {deliveries.length === 0 ? (
                <TableEmpty colSpan={6} message={t('webhooks.noDeliveries')} />
              ) : (
                deliveries.map((delivery) => {
                  const dStatus = getDeliveryStatusDisplay(delivery.status);
                  const deliveryId = delivery.entity?.id ?? '';
                  return (
                    <TableRow key={deliveryId}>
                      <TableCell>
                        <Badge variant={dStatus.variant === 'info' ? 'secondary' : dStatus.variant}>{t(dStatus.labelKey)}</Badge>
                      </TableCell>
                      <TableCell>
                        <code className={`text-xs font-mono ${delivery.response_status >= 400 ? 'text-destructive' : 'text-foreground'}`}>
                          {delivery.response_status || '---'}
                        </code>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDuration(delivery.duration_ms)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        #{delivery.attempt_number}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(delivery.attempted_at as any)}
                      </TableCell>
                      <TableCell className="text-right">
                        {delivery.status === 'WEBHOOK_DELIVERY_STATUS_FAILED' || delivery.status === 'WEBHOOK_DELIVERY_STATUS_EXHAUSTED' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              store.RetryDelivery(id!, deliveryId, {}).then(() => loadDeliveries());
                            }}
                          >
                            {t('webhooks.retry')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default WebhookDetailPage;
